import React, { memo, useEffect } from 'react';
import { Field } from 'redux-form';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { NavigationButton, NavigationButtonGroup } from 'web-ui-components/lib/navigation/buttons';
import { Input } from 'web-ui-components/lib/atoms/forms';
import { GenericFieldWrapper } from 'web-ui-components/lib/layout/fieldWrappers';
import { StandoutTile } from 'web-ui-components/lib/layout/content';
import { BulletList } from 'web-ui-components/lib/typography/lists';
import { FormSubmitButton } from '../../FormSubmitButton';
import { DIRECT_DEBIT_INSTRUCTIONS } from 'constants/paths';
import { setContentSquare } from 'utils/setContentSquare';

const duplicateUser = `Looks like someone’s taken that username already. Why not try something else and give it another go?`;

function InputFormField({
  input,
  meta: { touched, error },
  title,
  showUserNameExists,
  description,
  isDisabled,
  change
}) {
  return (
    <GenericFieldWrapper
      name="userName_header"
      title={title}
      description={description}
      validationMessage={showUserNameExists ? duplicateUser : touched && error}
    >
      <Input
        {...input}
        id="userName"
        type="text"
        disabled={isDisabled}
        onChange={(event) => {
          const { name, value } = event.target;
          change(name, value);
        }}
      />
    </GenericFieldWrapper>
  );
}

const ChooseUserNameScreen = memo(function ChooseUserNameScreen({
  form,
  onSubmit,
  handleSubmit,
  submitting,
  caseDetails,
  isUserNameChoosen,
  isSecondaryApplicant,
  change,
  showUserNameExists,
  navigateToScreen
}) {
  const description = !isUserNameChoosen
    ? `We’ve defaulted your username to your email address. Want to change it to something else? Go ahead.`
    : '';

  useEffect(() => {
    setContentSquare();
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageHeading>{isUserNameChoosen ? `Your username` : `Choose a username`}</PageHeading>
      {!isUserNameChoosen && (
        <StandoutTile.Guidance>
          <StandardBody>
            <p>{`It’s super important to remember your username as you’ll need it to:`}</p>
          </StandardBody>
          <BulletList
            items={[
              'Sign in to give us more information or see if your application has been successful',
              'Sign in to continue your application if you save your progress',
              'Sign in to internet banking'
            ]}
          />
        </StandoutTile.Guidance>
      )}
      <Field
        name={isSecondaryApplicant ? 'secondaryUserName' : 'userName'}
        type="text"
        title={`Username`}
        description={description}
        isDisabled={!caseDetails.showUserNameExists && isUserNameChoosen}
        showUserNameExists={caseDetails.showUserNameExists}
        change={(name, value) => {
          change(name, value);
          showUserNameExists(false);
        }}
        component={InputFormField}
      />
      <NavigationButtonGroup
        primaryButton={<FormSubmitButton formName={form} disabled={submitting} />}
        secondaryButtons={
          <NavigationButton.Secondary
            labelText={`Back`}
            onClick={(e) => {
              // prevent the form unintentionally submitting.
              /** @todo We wouldn't need to do this if Buttons had a type attribute */
              e.preventDefault();
              navigateToScreen(DIRECT_DEBIT_INSTRUCTIONS);
            }}
          />
        }
      />
    </form>
  );
});
export { ChooseUserNameScreen };
